import { DiagramComponent } from '../../core/DiagramComponent';
import type { LineConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

// Declare global JXG for JSXGraph constants
declare const JXG: {
  COORDS_BY_USER: number;
};

interface ExtendedLineConfig extends LineConfig {
  point1?: [number | string, number | string];
  point2?: [number | string, number | string];
  width?: number;
  extend?: boolean;
  attributes?: Record<string, any>;
}

interface JXGLine extends JXGElement {
  point1: {
    setPosition(type: number, coords: [number, number]): void;
  };
  point2: {
    setPosition(type: number, coords: [number, number]): void;
  };
}

/**
 * Line component
 */
export class Line extends DiagramComponent<ExtendedLineConfig> {
  constructor(config: ExtendedLineConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGLine {
    const config = this.config;
    const p1 = config.point1 || (config.points && config.points[0]) || [0, 0];
    const p2 = config.point2 || (config.points && config.points[1]) || [1, 1];

    const x1 = this.evaluateExpression(p1[0]);
    const y1 = this.evaluateExpression(p1[1]);
    const x2 = this.evaluateExpression(p2[0]);
    const y2 = this.evaluateExpression(p2[1]);

    const attributes = {
      strokeColor: config.color || '#000',
      strokeWidth: config.width || config.strokeWidth || 2,
      straightFirst: config.extend !== false,
      straightLast: config.extend !== false,
      ...config.attributes,
    };

    const line = this.board.create('line', [[x1, y1], [x2, y2]], attributes) as JXGLine;
    this.jsxElements.push(line);

    return line;
  }

  /**
   * Update line positions in place without recreating
   */
  protected _rerender(): void {
    if (this.jsxElements.length > 0) {
      const config = this.config;
      const p1 = config.point1 || (config.points && config.points[0]) || [0, 0];
      const p2 = config.point2 || (config.points && config.points[1]) || [1, 1];

      const x1 = Number(this.evaluateExpression(p1[0]));
      const y1 = Number(this.evaluateExpression(p1[1]));
      const x2 = Number(this.evaluateExpression(p2[0]));
      const y2 = Number(this.evaluateExpression(p2[1]));

      // Update the line's point positions
      const line = this.jsxElements[0] as JXGLine;
      line.point1.setPosition(JXG.COORDS_BY_USER, [x1, y1]);
      line.point2.setPosition(JXG.COORDS_BY_USER, [x2, y2]);
      
      this.board.update();
    } else {
      // First render
      this.render();
    }
  }

  getSpecificity(): number {
    return 80; // Lines have medium priority
  }
}
